import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, Formik } from "formik";
import { FC, useEffect, useState } from "react";
import "react-multi-carousel/lib/styles.css";
import { Initial } from "./CreateGroupModal";
import { GroupInfoDef, IMemberGroup } from "../../types/group.type";
import { getGroupMembers } from "../../api/group.api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { extractUsername } from "@/utils/extractUsername";
import { Checkbox } from "@/components/ui/checkbox";
import { OverflowMenuVertical, UserAvatar } from "@carbon/icons-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import InfiniteScroll from "react-infinite-scroll-component";
import { Loader2 } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { getMessage } from "@/utils/message";

type AddMembersModalProps = {
  openModal: boolean;
  rootGroup?: Partial<GroupInfoDef>;
  initialValues: Initial;
  pendingSubmitData: Initial | null;
  openAddMembers: boolean;
  setOpenAddMembers: (open: boolean) => void;
  createGroupFn: ({
    values,
    setSubmitting,
  }: {
    values: Initial;
    setSubmitting: (value: boolean) => void;
  }) => Promise<void>;
};

const AddMembersModal: FC<AddMembersModalProps> = ({
  openModal,
  rootGroup,
  initialValues,
  pendingSubmitData,
  openAddMembers,
  setOpenAddMembers,
  createGroupFn,
}) => {
  const rootGroupId = rootGroup?.rootGroupId ?? rootGroup?.id;
  const [membersList, setMembersList] = useState<IMemberGroup[]>([]);
  const [totalMembers, setTotalMembers] = useState(0);
  const [selectedMembers, setSelectedMembers] = useState<IMemberGroup[]>([]);
  const [ownerMember, setOwnerMember] = useState<IMemberGroup | null>(null);
  const [page, setPage] = useState(1);
  const [isInitSuccess, setIsInitSuccess] = useState(false);

  useEffect(() => {
    if (openModal) {
      setPage(1);
      setMembersList([]);
      setTotalMembers(0);
      setSelectedMembers([]);
      setOwnerMember(null);
      setIsInitSuccess(true);
    }
  }, [openModal]);

  useEffect(() => {
    if (isInitSuccess) {
      getGroupMembersList(1);
      setIsInitSuccess(false);
    }
  }, [isInitSuccess]);

  const getGroupMembersList = async (pageNumber?: number) => {
    if (!rootGroupId) return;
    const groupMembersList = await getGroupMembers({
      groupId: rootGroupId,
      pageNumber: pageNumber,
      pageSize: 10,
    });
    setMembersList([...membersList, ...groupMembersList.data]);
    setTotalMembers(groupMembersList.totalElements);
  };

  const updateSelectedMembers = (member: IMemberGroup) => {
    if (selectedMembers.some((m) => m.userId === member.userId)) {
      setSelectedMembers((prev) =>
        prev.filter((m) => m.userId !== member.userId)
      );
      if (ownerMember?.userId === member.userId) {
        setOwnerMember(null);
      }
    } else {
      setSelectedMembers((prev) => [...prev, member]);
    }
  };

  const handleSubmit = ({
    values,
    setSubmitting,
  }: {
    values: Initial;
    setSubmitting: (value: boolean) => void;
  }) => {
    const newValues = {
      ...values,
      membersSubGroup: selectedMembers
        .filter((member) => member.userId !== ownerMember?.userId)
        .map((member) => member.userId),
      ownerSubGroup: ownerMember?.userId,
    };

    if (!newValues.membersSubGroup?.length && !newValues.ownerSubGroup) {
      toast({
        variant: "error",
        description: getMessage("MSG189"),
      });
    } else if (!newValues.ownerSubGroup) {
      toast({
        variant: "error",
        description: getMessage("MSG190"),
      });
    } else {
      createGroupFn({ values: newValues, setSubmitting });
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={openAddMembers}>
      <DialogContent className="sm:max-w-[700px] bg-white p-0 gap-0 rounded-lg border border-gray-200 shadow-lg">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-lg font-semibold text-gray-900">
            Thêm thành viên nhóm
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600 mt-1">
            Nhập tên hoặc địa chỉ email của bạn bè mà bạn muốn mời họ vào Tổ
            chức
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-6 pt-0">
          <Formik
            initialValues={pendingSubmitData || initialValues}
            onSubmit={(values: Initial, { setSubmitting }) => {
              handleSubmit({ values, setSubmitting });
            }}
          >
            {({ isSubmitting, isValid }) => (
              <Form className="space-y-6">
                <div className="grid grid-cols-2 gap-4 h-[580px]">
                  <div className="flex flex-col gap-3 px-3 py-3 min-h-0 border border-secondary-200 rounded-md">
                    <div className="text-secondary-600 text-sm font-medium px-3">
                      Danh sách thành viên tổ chức:{" "}
                      <span className="text-secondary-600 text-xs font-semibold">
                        ({totalMembers})
                      </span>
                    </div>
                    <InfiniteScroll
                      height={522}
                      dataLength={membersList.length}
                      next={() => {
                        const nextPage = page + 1;
                        getGroupMembersList(nextPage);
                        setPage(nextPage);
                      }}
                      hasMore={membersList.length < totalMembers}
                      loader={
                        <div className="flex items-center justify-center mt-6">
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          <span className="text-sm text-secondary-600">
                            Đang tải thêm...
                          </span>
                        </div>
                      }
                      endMessage={
                        <div className="text-center py-4">
                          <span className="text-sm text-secondary-600">
                            Đã hiển thị tất cả thành viên
                          </span>
                        </div>
                      }
                    >
                      {membersList.map((member) => (
                        <div
                          key={member.userId}
                          className="cursor-default hover:bg-secondary-50 rounded-md px-3 py-2 flex items-center gap-3"
                        >
                          <Checkbox
                            id={member.userId.toString()}
                            checked={selectedMembers.some(
                              (m) => m.userId === member.userId
                            )}
                            onCheckedChange={() =>
                              updateSelectedMembers(member)
                            }
                          />
                          <UserItem user={member} />
                        </div>
                      ))}
                    </InfiniteScroll>
                  </div>
                  <div className="flex flex-col gap-3 px-3 py-3 min-h-0 border border-secondary-200 rounded-md">
                    <div className="text-secondary-600 text-sm font-medium px-3">
                      Danh sách chọn:{" "}
                      <span className="text-secondary-600 text-xs font-semibold">
                        ({selectedMembers.length})
                      </span>
                    </div>
                    <div className="flex flex-col overflow-y-auto">
                      {selectedMembers.map((member) => (
                        <div
                          key={member.userId}
                          className="cursor-default hover:bg-secondary-50 rounded-md px-3 py-2 flex items-center justify-between gap-3"
                        >
                          <UserItem
                            user={member}
                            isOwner={member.userId === ownerMember?.userId}
                          />
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                className="px-3 py-2"
                                variant="ghost"
                              >
                                <OverflowMenuVertical />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                className="cursor-pointer hover:bg-secondary-50 py-2 px-3"
                                onClick={() =>
                                  member.userId === ownerMember?.userId
                                    ? setOwnerMember(null)
                                    : setOwnerMember(member)
                                }
                              >
                                {member.userId === ownerMember?.userId
                                  ? "Gỡ quyền chủ sở hữu"
                                  : "Trao quyền chủ sở hữu"}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="cursor-pointer hover:bg-secondary-50 py-2 px-3"
                                onClick={() => updateSelectedMembers(member)}
                              >
                                Xóa thành viên
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <DialogFooter className="pt-4 gap-2">
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() => setOpenAddMembers(false)}
                    disabled={isSubmitting}
                    className="h-10 px-4 border-gray-200 text-gray-700 hover:bg-gray-50"
                  >
                    Quay lại
                  </Button>
                  <Button
                    type="submit"
                    disabled={!isValid || isSubmitting}
                    loading={isSubmitting}
                    className="h-10 px-4 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Tạo nhóm
                  </Button>
                </DialogFooter>
              </Form>
            )}
          </Formik>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const UserItem = ({
  user,
  isOwner,
}: {
  user: IMemberGroup;
  isOwner?: boolean;
}) => {
  return (
    <div className="flex gap-2 min-w-0">
      <Avatar className="h-10 w-10 rounded-full">
        <AvatarImage src={user.photoUrl || ""} />
        <AvatarFallback>{user.email.slice(0, 2)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <div className="flex items-center gap-1">
          <p
            className="text-sm font-medium text-secondary-900 truncate"
            title={user.name || extractUsername(user.email || "")}
          >
            {user.name || extractUsername(user.email || "")}
          </p>
          {isOwner && (
            <Badge className="font-medium text-[9px] whitespace-nowrap flex items-center gap-1">
              <UserAvatar size={12} />
              Chủ sở hữu
            </Badge>
          )}
        </div>
        <p
          className="text-xs text-secondary-600 truncate"
          title={user.email}
        >
          {user.email}
        </p>
      </div>
    </div>
  );
};

export default AddMembersModal;
